/**
 * MODAL: ASSIGN USER
 * Modale pour assigner un utilisateur avec recherche
 * Pattern basé sur AssignCrewModal pour cohérence UX
 */

import { useState, useMemo } from 'react';
import { Search, User } from 'lucide-react';
import { Modal } from '../ui/modal';
import { Badge } from '../ui';
import { useUsers } from '@/contexts/UsersContext';

interface AssignUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserId?: string | null;
  onAssign: (userId: string) => Promise<void>;
  recentUserIds?: string[];
  title?: string;
  showUnassignOption?: boolean;
  onUnassign?: () => Promise<void>;
}

export function AssignUserModal({
  isOpen,
  onClose,
  currentUserId,
  onAssign,
  recentUserIds = [],
  title = 'Assigner à un utilisateur',
  showUnassignOption = false,
  onUnassign,
}: AssignUserModalProps) {
  const { users, loading } = useUsers();
  const [searchTerm, setSearchTerm] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Filtrer et trier les utilisateurs
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Filtrer par terme de recherche
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = users.filter(
        (u) => u.full_name?.toLowerCase().includes(term) || u.email.toLowerCase().includes(term)
      );
    }

    // Trier : utilisateurs récents en premier, puis par nom
    return filtered.sort((a, b) => {
      const aIsRecent = recentUserIds.includes(a.id);
      const bIsRecent = recentUserIds.includes(b.id);

      if (aIsRecent && !bIsRecent) return -1;
      if (!aIsRecent && bIsRecent) return 1;

      // Sinon, trier par nom
      const aName = a.full_name || a.email;
      const bName = b.full_name || b.email;
      return aName.localeCompare(bName);
    });
  }, [users, searchTerm, recentUserIds]);

  async function handleAssign(userId: string) {
    setIsAssigning(true);
    try {
      await onAssign(userId);
      onClose();
      setSearchTerm('');
    } finally {
      setIsAssigning(false);
    }
  }

  async function handleUnassign() {
    if (!onUnassign) return;

    setIsAssigning(true);
    try {
      await onUnassign();
      onClose();
      setSearchTerm('');
    } finally {
      setIsAssigning(false);
    }
  }

  function handleClose() {
    setSearchTerm('');
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="md">
      {/* Champ de recherche */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un utilisateur..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
            autoFocus
            disabled={loading || isAssigning}
          />
        </div>
      </div>

      {/* Option "Retirer l'assignation" */}
      {showUnassignOption && currentUserId && onUnassign && (
        <button
          onClick={handleUnassign}
          disabled={isAssigning}
          className="w-full text-left p-3 mb-2 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="font-medium text-red-600">Retirer l'assignation</div>
        </button>
      )}

      {/* Liste des utilisateurs */}
      {loading ? (
        <div className="text-center py-8 text-sm text-zinc-500">Chargement...</div>
      ) : filteredUsers.length > 0 ? (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredUsers.map((user) => {
            const isRecent = recentUserIds.includes(user.id);
            const isCurrent = user.id === currentUserId;

            return (
              <button
                key={user.id}
                onClick={() => handleAssign(user.id)}
                disabled={isAssigning || isCurrent}
                className="w-full text-left p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="size-8 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                      <User className="size-4 text-zinc-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-zinc-900 truncate">
                        {user.full_name || user.email}
                      </div>
                      {user.full_name && (
                        <div className="text-sm text-zinc-500 truncate">{user.email}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isRecent && (
                      <Badge variant="secondary" className="text-xs">
                        Récent
                      </Badge>
                    )}
                    {isCurrent && (
                      <Badge variant="outline" className="text-xs">
                        Actuel
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-sm text-zinc-500">
          {searchTerm.trim() ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur disponible'}
        </div>
      )}
    </Modal>
  );
}
